import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import { apiService } from '../../services/api';

const inputStyles = {
  width: '50px',
  '& input': {
    padding: '14px 0',
    fontSize: '1.2rem',
    fontWeight: 'bold'
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: 2
      }
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.light'
    }
  }
};

const TwoFactorAuth = ({ onVerify }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const inputRefs = useRef(Array(6).fill(null));

  useEffect(() => {
    if (countdown !== null) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        localStorage.clear();
        window.location.reload();
      }
    }
  }, [countdown]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  const handleError = (message) => {
    setError(message);
    setOtp(['', '', '', '', '', '']);
    inputRefs.current[0].focus();

    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (newAttempts >= 2) {
      setRedirecting(true);
      setCountdown(3);
    } else {
      setError(`Invalid PIN. ${2 - newAttempts} attempt${2 - newAttempts === 1 ? '' : 's'} remaining`);
    }
  };

  const handlePinChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      if (value && index < 5) {
        setTimeout(() => {
          inputRefs.current[index + 1].focus();
        }, 10);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        setTimeout(() => {
          inputRefs.current[index - 1].focus();
        }, 10);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const enteredPin = otp.join('');
    try {
      const token = localStorage.getItem('auth_token');
      const loginId = localStorage.getItem('login_id');
      const twofaToken = localStorage.getItem('twofa_token');
      
      const response = await apiService.verify2FA(
        token, 
        enteredPin,
        loginId,
        twofaToken
      );
      
      if (response.status === 'success') {
        localStorage.setItem('auth_token', response.data.auth_token);
        localStorage.setItem('2faVerified', 'true');
        onVerify();
      } else {
        handleError('Invalid PIN');
      }
    } catch (err) {
      handleError('Verification failed');
    }
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        background: theme.palette.mode === 'dark' 
          ? `linear-gradient(45deg, ${alpha(theme.palette.primary.dark, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`
          : `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 4}
        sx={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '400px',
          height: isMobile ? '100vh' : 'auto',
          mx: isMobile ? 0 : 3,
          borderRadius: isMobile ? 0 : '16px',
          background: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header Section */}
        <Box sx={{ 
          px: 4,
          pt: isMobile ? 6 : 4,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}>
            <SecurityIcon 
              sx={{ 
                fontSize: 32,
                color: theme.palette.primary.main,
              }} 
            />
            <Typography 
              variant="h6"
              sx={{ 
                fontWeight: 600,
                letterSpacing: '0.5px',
                color: theme.palette.text.primary,
              }}
            >
              TRADEHEIST
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="textSecondary"
            align="center"
            sx={{ mt: 1 }}
          >
            Please enter your 6-digit security PIN to continue
          </Typography>
        </Box>

        {/* Form Section */}
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            p: 4,
            flex: 1,
            ...(isMobile && {
              height: 'calc(100vh - 140px)', // Increased subtraction to move content up
              justifyContent: 'space-between'
            })
          }}
        >
          {/* PIN Input Section */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 2
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1,
                justifyContent: 'center',
                mb: error ? 2 : 0
              }}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  autoFocus={false}
                  inputProps={{
                    maxLength: 1,
                    style: { 
                      textAlign: 'center',
                      fontSize: isMobile ? '1.25rem' : '1.5rem',
                      padding: '8px',
                      width: isMobile ? '32px' : '35px',
                      height: isMobile ? '32px' : '35px',
                      WebkitTextSecurity: 'disc'
                    }
                  }}
                  sx={inputStyles}
                />
              ))}
            </Box>

            {error && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                gap: 1,
                color: 'error.main',
                mt: 2
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                }}>
                  <LockIcon fontSize="small" />
                  <Typography variant="body2">
                    {error}
                  </Typography>
                </Box>
                {redirecting && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                  }}>
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="body2">
                      Too many failed attempts. Redirecting to login in {countdown} seconds...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Button Section */}
          <Box sx={{ 
            width: '100%',
            ...(isMobile ? {
              mb: 4  // Increased bottom margin
            } : {
              mt: 4
            })
          }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                py: 1.5,
                fontSize: '0.9375rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
                height: '48px'
              }}
            >
              Verify PIN
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TwoFactorAuth; 